import {
  Box,
  Typography,
  Chip,
  Button,
  CircularProgress,
  Alert,
  IconButton,
  Tooltip,
} from "@mui/material";
import {
  Refresh as RefreshIcon,
  Info as InfoIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Add as AddIcon,
} from "@mui/icons-material";
import React, { useState } from "react";
import {
  useGetIntegrationAccountsQuery,
  useDeleteIntegrationAccountMutation,
  IntegrationAccountDto,
} from "../../api/integrationAccountsApi";
import { DataTable, Column } from "../../components/common/DataTable/DataTable";
import PageTitle from "../../components/common/PageTitle";
import { IntegrationIcon } from "../../components/common/pageIcons";
import IntegrationAccountModal from "./components/IntegrationAccountModal";
import DeleteIntegrationAccountDialog from "./components/DeleteIntegrationAccountDialog";
import { getActiveStatusColor, getIntegrationSyncStatusColor } from "../../utils/statusColors";
import { useInfiniteDataTableItems } from "../../hooks/useInfiniteDataTableItems";
import { DEFAULT_DATA_TABLE_PAGE_SIZE } from "../../constants/dataTable";
import PageLayout from "../../components/layout/PageLayout";

const IntegrationAccountsPage: React.FC = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedAccount, setSelectedAccount] =
    useState<IntegrationAccountDto | null>(null);
  const [modalMode, setModalMode] = useState<"add" | "edit">("add");
  const [page, setPage] = useState(0);

  // API calls
  const {
    data: accountsData,
    isLoading,
    isFetching,
    error,
    refetch,
  } = useGetIntegrationAccountsQuery({
    pageNumber: page + 1,
    pageSize: DEFAULT_DATA_TABLE_PAGE_SIZE,
  });
  const { accumulatedItems: accounts, resetAccumulatedItems } =
    useInfiniteDataTableItems<IntegrationAccountDto>(accountsData?.items, page);

  const [deleteAccount, { isLoading: isDeleting }] =
    useDeleteIntegrationAccountMutation();

  const resetAccountsTable = () => {
    resetAccumulatedItems();
    setPage(0);
  };

  const handleAddAccount = () => {
    setSelectedAccount(null);
    setModalMode("add");
    setIsModalOpen(true);
  };

  const handleEditAccount = (account: IntegrationAccountDto) => {
    setSelectedAccount(account);
    setModalMode("edit");
    setIsModalOpen(true);
  };

  const handleDeleteAccount = (account: IntegrationAccountDto) => {
    setSelectedAccount(account);
    setIsDeleteModalOpen(true);
  };

  const handleModalClose = () => {
    resetAccountsTable();
    setIsModalOpen(false);
    setSelectedAccount(null);
  };

  const handleDeleteModalClose = () => {
    setIsDeleteModalOpen(false);
    setSelectedAccount(null);
  };

  const handleDeleteConfirm = async () => {
    if (selectedAccount) {
      try {
        await deleteAccount(selectedAccount.id).unwrap();
        resetAccountsTable();
        handleDeleteModalClose();
      } catch (error) {
        console.error("Failed to delete integration account:", error);
      }
    }
  };

  const formatDate = (dateString: string | null | undefined) => {
    if (!dateString) return "—";
    return new Date(dateString).toLocaleString();
  };

  const handleLoadMore = () => {
    if (accountsData?.hasNextPage && !isFetching) {
      setPage((currentPage) => currentPage + 1);
    }
  };

  const handleRefresh = () => {
    resetAccountsTable();
    refetch();
  };

  const columns: Column[] = [
    {
      field: "accountName",
      headerName: "Account Name",
      flex: 1.5,
      renderCell: (value: string) => (
        <Typography variant="body2" fontWeight={500}>
          {value}
        </Typography>
      ),
    },
    {
      field: "integrationTypeName",
      headerName: "Integration Type",
      flex: 1,
      renderCell: (value: string) => (
        <Chip label={value} color="primary" size="small" variant="outlined" />
      ),
    },
    {
      field: "apiKey",
      headerName: "API Key",
      flex: 1,
      renderCell: (value: string) => (
        <Typography
          variant="body2"
          fontFamily="monospace"
          color="text.secondary"
        >
          {value}
        </Typography>
      ),
    },
    {
      field: "isActive",
      headerName: "Status",
      width: 120,
      renderCell: (value: boolean) => (
        <span
          style={{
            color: getActiveStatusColor(value),
            fontWeight: "bold",
          }}
        >
          {value ? "Active" : "Inactive"}
        </span>
      ),
    },
    {
      field: "lastSyncedAt",
      headerName: "Last Synced",
      flex: 1,
      renderCell: (value: string | null) => (
        <Typography variant="body2" color="text.secondary">
          {formatDate(value)}
        </Typography>
      ),
    },
    {
      field: "lastSyncStatus",
      headerName: "Sync Status",
      width: 120,
      renderCell: (value: string | null) => {
        if (!value) return <Typography color="text.secondary">—</Typography>;

        return (
          <span
            style={{
              color: getIntegrationSyncStatusColor(value),
              fontWeight: "bold",
            }}
          >
            {value}
          </span>
        );
      },
    },
  ];

  const getActions = (account: IntegrationAccountDto) => [
    {
      icon: <EditIcon fontSize="small" />,
      label: "Edit Account",
      onClick: handleEditAccount,
      color: "primary" as const,
    },
    {
      icon: <DeleteIcon fontSize="small" />,
      label: "Delete Account",
      onClick: handleDeleteAccount,
      color: "error" as const,
    },
  ];

  // Loading state
  if (isLoading && !accountsData) {
    return (
      <PageLayout>
        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
          <CircularProgress />
        </Box>
      </PageLayout>
    );
  }

  // Error state
  if (error) {
    return (
      <PageLayout>
        <Alert severity="error" sx={{ mb: 2 }}>
          Failed to load integration accounts. Please try again.
        </Alert>
        <Button onClick={handleRefresh} startIcon={<RefreshIcon />}>
          Retry
        </Button>
      </PageLayout>
    );
  }

  return (
    <PageLayout>
      <PageTitle
        title="Integration Accounts"
        icon={<IntegrationIcon />}
        endAdornment={
          <Tooltip title="Manage external integration accounts (API keys) for services like Samsara, EvaEld, etc.">
            <IconButton size="small" sx={{ color: "text.secondary" }}>
              <InfoIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        }
        actions={
          <Box sx={{ display: "flex", gap: 2 }}>
            <Tooltip title="Refresh data">
              <IconButton onClick={handleRefresh} disabled={isLoading}>
                <RefreshIcon />
              </IconButton>
            </Tooltip>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleAddAccount}
            >
              Add Integration Account
            </Button>
          </Box>
        }
      />

      <DataTable
        items={accounts}
        columns={columns}
        actions={getActions}
        isLoading={isLoading && accounts.length === 0}
        title="Integration Accounts"
        totalCount={accountsData?.totalCount}
        hasNextPage={accountsData?.hasNextPage}
        isLoadingMore={isFetching && accounts.length > 0}
        onLoadMore={handleLoadMore}
      />

      {/* Integration Account Modal */}
      <IntegrationAccountModal
        open={isModalOpen}
        account={selectedAccount}
        mode={modalMode}
        onClose={handleModalClose}
      />

      {/* Delete Confirmation Dialog */}
      <DeleteIntegrationAccountDialog
        open={isDeleteModalOpen}
        account={selectedAccount}
        onClose={handleDeleteModalClose}
        onConfirm={handleDeleteConfirm}
        isLoading={isDeleting}
      />
    </PageLayout>
  );
};

export default IntegrationAccountsPage;

